/**
 * SplitViewRenderer
 *
 * Renders the split view using react-resizable-panels.
 * Supports unlimited nested horizontal/vertical splits.
 */

import React, {
  useCallback,
  useState,
  useRef,
  createContext,
  useContext,
  useEffect,
} from "react";
import { Panel, Group, Separator } from "react-resizable-panels";
import { motion, AnimatePresence } from "framer-motion";
import { useSplitView } from "./SplitViewProvider";
import PanelContent from "./PanelContent";
import SplitPanelHeader from "./SplitPanelHeader";
import TabSlider from "./TabSlider";
import EdgeDropZone from "./EdgeDropZone";
import {
  SplitNode,
  SplitPanel as SplitPanelType,
  SplitGroupNode,
  SplitOrientation,
  useHasMultipleTabs,
  useTabCount,
  useCurrentTabIndex,
  useCurrentTabId,
  useTabsActions,
} from "@/hooks/use-tabs-store";
import { useGestureScroll } from "@/hooks/use-gesture-scroll";
import {
  isSidebarItemDrag,
  getDragPayload,
  DragPayload,
} from "@/utils/drag-types";
import { useIsInsideTabContent } from "@/components/ui/tabs";

// ============================================================================
// Drag Context - Track which panel is being dragged
// ============================================================================

interface DragContextValue {
  draggingPanelId: string | null;
  setDraggingPanelId: (id: string | null) => void;
}

const DragContext = createContext<DragContextValue>({
  draggingPanelId: null,
  setDraggingPanelId: () => {},
});

// ============================================================================
// Types
// ============================================================================

interface SplitViewRendererProps {
  /** @deprecated Children are no longer used - content is always rendered through panels */
  children?: React.ReactNode;
}

interface PanelWrapperProps {
  panel: SplitPanelType;
  isFocused: boolean;
  onClose: () => void;
  onFocus: () => void;
  onMovePanel: (
    sourcePanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  onDropSidebarItem: (
    payload: DragPayload,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  onOpenInNewTab: () => void;
  onOpenInNewWindow: () => void;
  onMovePanelToTab: (tabId: string | "new") => void;
}

type DropPosition = "left" | "right" | "top" | "bottom" | null;

// ============================================================================
// Drop Zone Overlay Component
// ============================================================================

interface DropZoneOverlayProps {
  activeZone: DropPosition;
}

const DropZoneOverlay: React.FC<DropZoneOverlayProps> = ({ activeZone }) => {
  if (!activeZone) return null;

  const positionStyles: Record<string, React.CSSProperties> = {
    left: { top: 0, left: 0, bottom: 0, width: "50%" },
    right: { top: 0, right: 0, bottom: 0, width: "50%" },
    top: { top: 0, left: 0, right: 0, height: "50%" },
    bottom: { bottom: 0, left: 0, right: 0, height: "50%" },
  };

  return (
    <div
      className="border-2 border-dashed border-accent"
      style={{
        position: "absolute",
        borderRadius: "6px",
        transition: "all 150ms ease",
        pointerEvents: "none",
        margin: "4px",
        backgroundColor:
          "color-mix(in srgb, var(--arc-accent, #6366F1) 15%, transparent)",
        ...positionStyles[activeZone],
      }}
    />
  );
};

// ============================================================================
// Panel Wrapper Component
// ============================================================================

const PanelWrapper: React.FC<PanelWrapperProps> = ({
  panel,
  isFocused,
  onClose,
  onFocus,
  onMovePanel,
  onDropSidebarItem,
  onOpenInNewTab,
  onOpenInNewWindow,
  onMovePanelToTab,
}) => {
  const { draggingPanelId, setDraggingPanelId } = useContext(DragContext);
  const { panelCount } = useSplitView();
  const hasMultipleTabs = useHasMultipleTabs();
  const isInsideTabContent = useIsInsideTabContent();
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropPosition, setDropPosition] = useState<DropPosition>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);

  // Only show header when there are multiple panels in the current view, and not inside a tab content
  const showHeader = panelCount > 1 && !isInsideTabContent;

  // Check if this panel is being dragged
  const isSelf = draggingPanelId === panel.id;

  const handleDragStateChange = useCallback(
    (isDragging: boolean) => {
      setDraggingPanelId(isDragging ? panel.id : null);
    },
    [panel.id, setDraggingPanelId],
  );

  // Check if it's a panel drag or sidebar drag
  const isPanelDrag = (e: React.DragEvent): boolean => {
    return e.dataTransfer.types.includes("application/x-split-panel");
  };

  const isSidebarDrag = (e: React.DragEvent): boolean => {
    return isSidebarItemDrag(e);
  };

  const getDropPosition = (e: React.DragEvent): DropPosition => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;

    // Calculate distance from each edge as a percentage
    const leftDist = x / width;
    const rightDist = 1 - leftDist;
    const topDist = y / height;
    const bottomDist = 1 - topDist;

    // Find the closest edge (within 30% threshold)
    const threshold = 0.3;
    const minDist = Math.min(leftDist, rightDist, topDist, bottomDist);

    if (minDist > threshold) return null; // Center zone - no drop

    if (minDist === leftDist) return "left";
    if (minDist === rightDist) return "right";
    if (minDist === topDist) return "top";
    if (minDist === bottomDist) return "bottom";

    return null;
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;

    // Accept panel drag (not self) or sidebar drag
    const acceptPanelDrag = isPanelDrag(e) && !isSelf;
    const acceptSidebarDrag = isSidebarDrag(e) && !isPanelDrag(e);

    if (acceptPanelDrag || acceptSidebarDrag) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
      setDropPosition(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    const isPanelType = isPanelDrag(e);
    const isSidebarType = isSidebarDrag(e);

    // Don't allow panel drop on self
    if (isPanelType && isSelf) {
      e.dataTransfer.dropEffect = "none";
      setDropPosition(null);
      return;
    }

    // Handle both panel drag and sidebar drag
    if (isPanelType || isSidebarType) {
      const position = getDropPosition(e);
      // Only allow drop on edges, not center
      if (position) {
        e.dataTransfer.dropEffect = isPanelType ? "move" : "copy";
        setDropPosition(position);
        setIsDragOver(true);
      } else {
        e.dataTransfer.dropEffect = "none";
        setDropPosition(null);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);

    const position = dropPosition;
    setDropPosition(null);

    if (!position) return;

    // Check if it's a panel drag
    const sourcePanelId = e.dataTransfer.getData("application/x-split-panel");
    if (sourcePanelId && sourcePanelId !== panel.id) {
      onMovePanel(sourcePanelId, position);
      return;
    }

    // Check if it's a sidebar drag
    const sidebarPayload = getDragPayload(e);
    if (sidebarPayload) {
      onDropSidebarItem(sidebarPayload, panel.id, position);
    }
  };

  return (
    <div
      ref={containerRef}
      data-panel-id={panel.id}
      className={`
        h-full w-full flex flex-col overflow-hidden relative
        ${isFocused ? "ring-2 ring-accent ring-inset" : ""}
        transition-shadow duration-150
      `}
      onClick={onFocus}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {showHeader && (
        <SplitPanelHeader
          panel={panel}
          isFocused={isFocused}
          onClose={onClose}
          onFocus={onFocus}
          onDragStateChange={handleDragStateChange}
          panelRef={containerRef}
          onOpenInNewTab={onOpenInNewTab}
          onOpenInNewWindow={onOpenInNewWindow}
          onMovePanelToTab={onMovePanelToTab}
        />
      )}
      <div className="flex-1 overflow-hidden">
        <PanelContent panel={panel} isFocused={isFocused} />
      </div>
      {isDragOver && <DropZoneOverlay activeZone={dropPosition} />}
    </div>
  );
};

// ============================================================================
// Recursive Node Renderer
// ============================================================================

interface NodeRendererProps {
  node: SplitNode;
  focusedPanelId: string | null;
  onFocusPanel: (panelId: string) => void;
  onClosePanel: (panelId: string) => void;
  onMovePanel: (
    sourcePanelId: string,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  onDropSidebarItem: (
    payload: DragPayload,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  onOpenInNewTab: (panelId: string) => void;
  onOpenInNewWindow: (panelId: string) => void;
  onMovePanelToTab: (panelId: string, tabId: string | "new") => void;
}

const NodeRenderer: React.FC<NodeRendererProps> = ({
  node,
  focusedPanelId,
  onFocusPanel,
  onClosePanel,
  onMovePanel,
  onDropSidebarItem,
  onOpenInNewTab,
  onOpenInNewWindow,
  onMovePanelToTab,
}) => {
  if (node.type === "panel") {
    return (
      <PanelWrapper
        panel={node.panel}
        isFocused={focusedPanelId === node.panel.id}
        onClose={() => onClosePanel(node.panel.id)}
        onFocus={() => onFocusPanel(node.panel.id)}
        onMovePanel={(sourcePanelId, position) =>
          onMovePanel(sourcePanelId, node.panel.id, position)
        }
        onDropSidebarItem={(payload, targetPanelId, position) =>
          onDropSidebarItem(payload, targetPanelId, position)
        }
        onOpenInNewTab={() => onOpenInNewTab(node.panel.id)}
        onOpenInNewWindow={() => onOpenInNewWindow(node.panel.id)}
        onMovePanelToTab={(tabId) => onMovePanelToTab(node.panel.id, tabId)}
      />
    );
  }

  // It's a group
  const group = node as SplitGroupNode;

  return (
    <Group id={group.id} orientation={group.orientation}>
      {group.children.map((child, index) => {
        const defaultSize = group.sizes[index] ?? 100 / group.children.length;
        const panelId =
          child.type === "panel"
            ? child.panel.id
            : `nested-${group.id}-${index}`;

        return (
          <React.Fragment key={panelId}>
            <Panel id={panelId} defaultSize={defaultSize} minSize={10}>
              <NodeRenderer
                node={child}
                focusedPanelId={focusedPanelId}
                onFocusPanel={onFocusPanel}
                onClosePanel={onClosePanel}
                onMovePanel={onMovePanel}
                onDropSidebarItem={onDropSidebarItem}
                onOpenInNewTab={onOpenInNewTab}
                onOpenInNewWindow={onOpenInNewWindow}
                onMovePanelToTab={onMovePanelToTab}
              />
            </Panel>
            {index < group.children.length - 1 && (
              <Separator
                style={{
                  background: "var(--arc-border, #1f2937)",
                  width: group.orientation === "horizontal" ? "4px" : undefined,
                  height: group.orientation === "vertical" ? "4px" : undefined,
                  outline: "none",
                }}
                className="hover:bg-accent active:bg-accent/80 focus:bg-accent focus:outline-none transition-colors"
              />
            )}
          </React.Fragment>
        );
      })}
    </Group>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const SplitViewRenderer: React.FC<SplitViewRendererProps> = () => {
  const {
    isActive,
    root,
    focusedPanelId,
    focusPanel,
    closePanel,
    movePanel,
    openInSplitViewAtPosition,
    openInNewTab,
    openInNewWindow,
    movePanelToTab,
  } = useSplitView();

  const hasMultipleTabs = useHasMultipleTabs();
  const tabCount = useTabCount();
  const currentTabIndex = useCurrentTabIndex();
  const currentTabId = useCurrentTabId();
  const { goToPreviousTab, goToNextTab } = useTabsActions();
  const [draggingPanelId, setDraggingPanelId] = useState<string | null>(null);

  // Track direction for animations
  const [tabDirection, setTabDirection] = useState(0);
  const prevIndexRef = useRef(currentTabIndex);

  useEffect(() => {
    if (currentTabIndex > prevIndexRef.current) {
      setTabDirection(1); // Moving right
    } else if (currentTabIndex < prevIndexRef.current) {
      setTabDirection(-1); // Moving left
    } else {
      setTabDirection(0);
    }
    prevIndexRef.current = currentTabIndex;
  }, [currentTabIndex]);

  // Ref for the main content area to capture scroll gestures
  const contentRef = useRef<HTMLDivElement>(null);

  // Use gesture scroll hook for horizontal tab navigation
  // Attached to contentRef which wraps all content when multiple tabs exist
  useGestureScroll(contentRef, {
    direction: "horizontal",
    totalPages: tabCount,
    currentIndex: currentTabIndex,
    onPrevious: goToPreviousTab,
    onNext: goToNextTab,
    disabled: tabCount <= 1,
  });

  const handleFocusPanel = useCallback(
    (panelId: string) => {
      focusPanel(panelId);
    },
    [focusPanel],
  );

  const handleClosePanel = useCallback(
    (panelId: string) => {
      closePanel(panelId);
    },
    [closePanel],
  );

  const handleMovePanel = useCallback(
    (
      sourcePanelId: string,
      targetPanelId: string,
      position: "left" | "right" | "top" | "bottom",
    ) => {
      movePanel(sourcePanelId, targetPanelId, position);
    },
    [movePanel],
  );

  const handleDropSidebarItem = useCallback(
    (
      payload: DragPayload,
      targetPanelId: string,
      position: "left" | "right" | "top" | "bottom",
    ) => {
      // Convert position to orientation
      const orientation: SplitOrientation =
        position === "left" || position === "right" ? "horizontal" : "vertical";
      openInSplitViewAtPosition(
        payload,
        targetPanelId,
        orientation,
        position === "left" || position === "top",
      );
    },
    [openInSplitViewAtPosition],
  );

  const handleOpenInNewTab = useCallback(
    (panelId: string) => {
      openInNewTab(panelId);
    },
    [openInNewTab],
  );

  const handleOpenInNewWindow = useCallback(
    (panelId: string) => {
      openInNewWindow(panelId);
    },
    [openInNewWindow],
  );

  const handleMovePanelToTab = useCallback(
    (panelId: string, tabId: string | "new") => {
      movePanelToTab(panelId, tabId);
    },
    [movePanelToTab],
  );

  // Always render through the panel system
  // The root will always exist because syncWithRoute ensures a panel is created
  // If root is somehow null (during initial render), show a loading state briefly
  if (!root) {
    return (
      <div
        ref={contentRef}
        style={{
          height: "100%",
          width: "100%",
          position: "relative",
          overscrollBehaviorX: "none",
        }}
      >
        <div style={{ height: "100%", width: "100%", overflow: "auto" }}>
          {/* Brief loading state while syncWithRoute initializes the panel */}
          <div className="flex items-center justify-center h-full bg-surface">
            <div className="animate-spin w-8 h-8 border-4 border-[var(--arc-accent)] border-t-transparent rounded-full" />
          </div>
        </div>
        {hasMultipleTabs && <TabSlider />}
      </div>
    );
  }

  return (
    <DragContext.Provider value={{ draggingPanelId, setDraggingPanelId }}>
      <EdgeDropZone>
        <div
          ref={contentRef}
          style={{
            height: "100%",
            width: "100%",
            position: "relative",
            // Prevent browser swipe navigation when we handle tab switching
            overscrollBehaviorX: "none",
          }}
        >
          {/* Split view content with tab animations */}
          {/* Only animate when there's a real directional tab switch;
              tabDirection === 0 means same-index change (e.g. hydration, ID regeneration)
              which would cause both old and new views to stack at the same position */}
          {tabDirection !== 0 ? (
            <AnimatePresence mode="popLayout" custom={tabDirection}>
              <motion.div
                key={currentTabId}
                custom={tabDirection}
                initial={{
                  x: tabDirection > 0 ? "30%" : "-30%",
                  opacity: 0.3,
                }}
                animate={{
                  x: 0,
                  opacity: 1,
                }}
                exit={{
                  x: tabDirection < 0 ? "30%" : "-30%",
                  opacity: 0.3,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  opacity: { duration: 0.2 },
                }}
                style={{
                  height: "100%",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <NodeRenderer
                  node={root}
                  focusedPanelId={focusedPanelId}
                  onFocusPanel={handleFocusPanel}
                  onClosePanel={handleClosePanel}
                  onMovePanel={handleMovePanel}
                  onDropSidebarItem={handleDropSidebarItem}
                  onOpenInNewTab={handleOpenInNewTab}
                  onOpenInNewWindow={handleOpenInNewWindow}
                  onMovePanelToTab={handleMovePanelToTab}
                />
              </motion.div>
            </AnimatePresence>
          ) : (
            <div
              style={{
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <NodeRenderer
                node={root}
                focusedPanelId={focusedPanelId}
                onFocusPanel={handleFocusPanel}
                onClosePanel={handleClosePanel}
                onMovePanel={handleMovePanel}
                onDropSidebarItem={handleDropSidebarItem}
                onOpenInNewTab={handleOpenInNewTab}
                onOpenInNewWindow={handleOpenInNewWindow}
                onMovePanelToTab={handleMovePanelToTab}
              />
            </div>
          )}

          {/* Tab slider island - shown when multiple tabs exist or split view is active */}
          {(hasMultipleTabs || isActive) && <TabSlider />}
        </div>
      </EdgeDropZone>
    </DragContext.Provider>
  );
};

export default SplitViewRenderer;
